import { useMemo, useState, type ReactNode } from 'react';
import { Link } from '@/shared/ui/link';
import { useI18n } from '@locales/client';
import { MOBILE_BREAKPOINT } from '@/shared/hooks/useBreakpoints';
import { getMediaQueryStore } from '@/shared/libs/get-media-query-store';
import { Image } from '@/shared/ui/image/Image';
import { BodyTextBoldSecondary } from '@/shared/ui/typography';
import {
  Section,
  SectionButton,
  SectionDescription,
  SectionTitle,
  SectionContent,
  ImageElement,
  ImageBonus,
  DisabledOverlay,
  InfoPseudoButton,
  CardFooter,
  CardFooterRow,
  CardActions,
  WishlistSlot,
} from './styles';

import { BonusNotWageringInfo } from '@/features/bonus-not-wagering/bonus-not-wagering-info';
import { brandIsPh, brandIsPh2 } from '@/shared/config/brand-by-target.mjs';
import { setGameEnterMode } from './model/set-game-enter-mode';
import type { useGameAccessCheck } from '@/shared/hooks/useGameAccessCheck';

const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT}px)`;
const PLAY_ICON_SRC = '/images/icons/play.svg';
const PLAY_ICON_SIZE = 16;
const GAME_IMAGE_SIZES = '(max-width: 768px) 106px, 168px';
const GAME_IMAGE_LIST = ['desktop', 'mobile', 'large'];

const handlePlayClick = () => setGameEnterMode('play');
const handleDemoClick = () => setGameEnterMode('demo');

export type GameProps = {
  hasScroll: boolean;
  wishlistToggleSlot: ReactNode;
  game: any;
  accessHelpers: ReturnType<typeof useGameAccessCheck>;
  activeBonusId?: number | null;
  priority?: boolean;
};

const Game = ({
  wishlistToggleSlot,
  hasScroll,
  game,
  accessHelpers,
  activeBonusId,
  priority,
}: GameProps) => {
  const t = useI18n();
  const [isHovered, setIsHovered] = useState(false);
  const isActiveBonus = activeBonusId === game?.id;

  const { infoPath, isDisabled, hasBonus } = useMemo(
    () => ({
      infoPath: accessHelpers.getGameInfoPath(game),
      isDisabled: accessHelpers.isDisabled(game),
      hasBonus: accessHelpers.hasBonus(game),
    }),
    [game, accessHelpers]
  );

  const handleMouseEnter = () => {
    if (!getMediaQueryStore(MOBILE_QUERY).getSnapshot()) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!getMediaQueryStore(MOBILE_QUERY).getSnapshot()) {
      setIsHovered(false);
    }
  };

  return (
    <Section
      $hasScroll={hasScroll}
      $isActive={isActiveBonus}
      $disabledGame={isDisabled}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <DisabledOverlay $isHovered={isHovered} $disabledGame={isDisabled}>
        <BonusNotWageringInfo isHovered={isHovered} game={game} />
      </DisabledOverlay>

      <SectionContent>
        <SectionTitle>{game?.title}</SectionTitle>
        <SectionDescription>{game?.providerName}</SectionDescription>

        <Link href={infoPath} onClick={handlePlayClick}>
          <SectionButton>
            <Image
              width={PLAY_ICON_SIZE}
              height={PLAY_ICON_SIZE}
              src={PLAY_ICON_SRC}
              alt=""
            />
            {t('common.game.card.play')}
          </SectionButton>
        </Link>

        <CardFooter>
          <CardFooterRow>
            {(!brandIsPh || brandIsPh2) && (
              <Link href={infoPath} onClick={handleDemoClick}>
                <BodyTextBoldSecondary>
                  {t('common.game.card.demo')}
                </BodyTextBoldSecondary>
              </Link>
            )}

            <CardActions>
              <Link href={infoPath}>
                <InfoPseudoButton />
              </Link>
              <WishlistSlot>{wishlistToggleSlot}</WishlistSlot>
            </CardActions>
          </CardFooterRow>
        </CardFooter>
      </SectionContent>

      <Link href={infoPath}>
        <ImageElement
          game={game}
          alt={game?.title}
          sizes={GAME_IMAGE_SIZES}
          src={game?.imageUrl}
          imgList={GAME_IMAGE_LIST}
          priority={priority}
        />
      </Link>

      {hasBonus && <ImageBonus $hasBonus={hasBonus} />}
    </Section>
  );
};

export { Game };
